import { AppSettings, AsamcoAuthService, ServerSetting } from '@asamco/base';
import * as signalR from '@microsoft/signalr';
import { from, Observable, of } from 'rxjs';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';

import { SignalrHook } from './signalr-hook';
import { SignalRLogger } from './signalr-logger';

export abstract class AsamcoSignalrBase {
	/** The name of the hub to subscribe on */
	protected abstract hubName: string;

	/** Events to register */
	protected abstract events: SignalrHook[];

	hubConnection: signalR.HubConnection;

	onInitialized: Observable<boolean>;

	/**
	 *Creates an instance of AsamcoSignalrBase.
	 * @param {AsamcoAuthService} auth
	 * @param {AppSettings} app
	 * @memberof AsamcoSignalrBase
	 */
	protected constructor(protected auth: AsamcoAuthService, protected app: AppSettings) {}

	protected getBackend(): ServerSetting {
		return this.auth.userSession.backend;
	}

	protected getAccessTokenFactory() {
		return () => this.auth.getPrimaryTokenAsync();
	}

	/**
	 * Starts the service
	 */
	public startService(): Observable<boolean> {
		// return if signalR is disabled
		if ((this.app.settings.isSignalREnabled || false) === false) return of(null);

		// Subscribe to isLoggedIn$ to be notified when all backends are logged in or out to
		// If logged in, connect to all backends
		// if logged out, remove all connections

		const connectStart = this.auth.isLoggedIn$.pipe(
			switchMap(isLoggedIn => {
				if (isLoggedIn) {
					return from(this.createConnection()).pipe(map(() => true));
				} else {
					this.stopService();
					return of(false);
				}
			})
		);

		this.onInitialized = connectStart.pipe(first(), map(() => true), shareReplay());
		this.onInitialized.subscribe();

		return this.onInitialized;
	}

	/**
	 * Stops the service
	 */
	protected stopService(): Promise<void> {
		if (this.hubConnection != null && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
			return this.hubConnection.stop();
		}
		return Promise.resolve();
	}

	/**
	 * Create a signalR connection to the backend
	 * @param backend
	 */
	private createConnection(): Promise<void> {
		// Get the actual base of the base url
		const baseUrl = this.getBackend().baseUrl.replace(/\/api\/?$/, '');

		if (this.hubConnection != null && this.hubConnection.state === signalR.HubConnectionState.Connected) {
			this.hubConnection.stop();
		}

		this.hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(baseUrl + '/hubs/' + this.hubName, {
				accessTokenFactory: this.getAccessTokenFactory(),
			})
			.withAutomaticReconnect({nextRetryDelayInMilliseconds: retryContext => {
				if(retryContext.retryReason.message === `Failed to complete negotiation with the server: Error: Unauthorized: Status code '401'` || retryContext.previousRetryCount > 5) {
					return null;
				}
				return 2000;
			}})
			.configureLogging(new SignalRLogger())
			.build();

		return this.hubConnection
			.start()
			.then(_ => {
				this.registerEvents(this.hubConnection);
				this.hubConnection.onreconnecting(err => console.warn('SignalR hub disconnected, attempting to reconnect...', err));
				this.hubConnection.onreconnected(_ => {
					console.warn(`Managed to reconnect to SignalR service for hub "${this.hubName}", re-initializing...`);
					this.handleReconnect();
				});
			});
	}

	/**
	 * Register hub events
	 */
	registerEvents(connection: signalR.HubConnection) {
		this.events.forEach(ev => {
			connection.on(ev.methodName, ev.newMethod);
		});
	}

	/**
	 * Handle reconnect after api disconnect, to be overwritten by the inheritor
	 */
	protected handleReconnect() {
		// NOSONAR - this is a hook
	}
}
